import { CanvasRendererFactory, CanvasState } from '../canvasrender';
import {
  CanvasOpAndroid as CanvasOp,
  PorterDuffMode,
  ShaderType,
  TileMode,
  Flags,
  TextStyleFlags,
} from '@fullstory/mobile-flatbuffers';
import { Canvas, CanvasAndroid, Rect, BitmapType } from '../view';
import { Matrix } from '../matrix';
import { NinePatch } from './9patch';
import { OffscreenCanvasCache } from '../offscreencanvascache';
import { logOnce, logWarnOnce } from '../log_once';

enum RegionOp {
  DIFFERENCE = 0,
  INTERSECT,
  UNION,
  XOR,
  REVERSE_DIFFERENCE,
  REPLACE,
}

export class AndroidCanvasRendererFactory extends CanvasRendererFactory<CanvasOp> {
  renderers: { [type: number]: Renderer } = {};

  constructor() {
    super();
    this.renderers[CanvasOp.SAVE] = new SaveRenderer();
    this.renderers[CanvasOp.SAVE_LAYER] = new SaveLayerRenderer();
    this.renderers[CanvasOp.SAVE_LAYER_ALPHA] = new SaveLayerAlphaRenderer();
    this.renderers[CanvasOp.RESTORE] = new RestoreRenderer();
    this.renderers[CanvasOp.RESTORE_TO_COUNT] = new RestoreToCountRenderer();

    this.renderers[CanvasOp.TRANSLATE] = new TranslateRenderer();
    this.renderers[CanvasOp.SCALE] = new ScaleRenderer();
    this.renderers[CanvasOp.ROTATE] = new RotateRenderer();
    this.renderers[CanvasOp.SET_MATRIX] = new SetMatrixRenderer();
    this.renderers[CanvasOp.CONCAT_MATRIX] = new ConcatMatrixRenderer();
    this.renderers[CanvasOp.CLIP_RECT] = new ClipRectRenderer();
    this.renderers[CanvasOp.WARNING] = new WarningRenderer();
    this.renderers[CanvasOp.CLIP_PATH] = new ClipPathRenderer();

    // Not technically a drawing op
    this.renderers[CanvasOp.DRAW_CHILD] = new DrawChildRenderer();

    // If you add something here, make sure you add it to visibleForInspectMode() below!
    this.renderers[CanvasOp.DRAW_ARC] = new DrawArcRenderer();
    this.renderers[CanvasOp.DRAW_BITMAP_POS] = new DrawBitmapPosRenderer();
    this.renderers[CanvasOp.DRAW_BITMAP_RECT] = new DrawBitmapRectRenderer();
    this.renderers[CanvasOp.DRAW_COLOR] = new DrawColorRenderer();
    this.renderers[CanvasOp.DRAW_LINE] = new DrawLineRenderer();
    this.renderers[CanvasOp.DRAW_OVAL] = new DrawOvalRenderer();
    this.renderers[CanvasOp.DRAW_PAINT] = new DrawPaintRenderer();
    this.renderers[CanvasOp.DRAW_TEXT] = new DrawTextRenderer();
    this.renderers[CanvasOp.DRAW_RECT] = new DrawRectRenderer();
    this.renderers[CanvasOp.DRAW_ROUND_RECT] = new DrawRoundRectRenderer();
    this.renderers[CanvasOp.DRAW_CIRCLE] = new DrawCircleRenderer();
    this.renderers[CanvasOp.DRAW_PATH] = new DrawPathRenderer();
    this.renderers[CanvasOp.DRAW_PATCH] = new DrawPatchRenderer();
    this.renderers[CanvasOp.DRAW_VIEW] = new DrawViewRenderer();
  }

  visibleForInspectMode(op: CanvasAndroid) {
    switch (op.op) {
      case CanvasOp.DRAW_RECT:
        // some views have PAINT_NULL as token to indicate the paint data was null at capture.
        // during unpack we set isDefault to true and give them color "transparent"
        // JPC also produces some views with non-null paint but which have no data set,
        // which also end up with transparent color
        const actuallyDraws =
          op.paint && (op.paint.bitmap || op.paint.shader || op.paint.color !== 'transparent');
        return !!actuallyDraws;
      case CanvasOp.DRAW_ARC:
      case CanvasOp.DRAW_BITMAP_POS:
      case CanvasOp.DRAW_BITMAP_RECT:
      case CanvasOp.DRAW_COLOR:
      case CanvasOp.DRAW_LINE:
      case CanvasOp.DRAW_OVAL:
      case CanvasOp.DRAW_PAINT:
      case CanvasOp.DRAW_TEXT:
      case CanvasOp.DRAW_ROUND_RECT:
      case CanvasOp.DRAW_CIRCLE:
      case CanvasOp.DRAW_PATH:
      case CanvasOp.DRAW_PATCH:
      case CanvasOp.DRAW_VIEW:
        return true;
      default:
        return false;
    }
  }

  drawsChild(op: Canvas<CanvasOp>) {
    const opAndroid = <CanvasAndroid>op;
    switch (op.op) {
      case CanvasOp.DRAW_CHILD:
        return opAndroid.viewId;
      default:
        return null;
    }
  }

  render(state: CanvasState, entry: CanvasAndroid) {
    const renderer = this.renderers[entry.op];
    if (!renderer) {
      logWarnOnce(
        `No renderer for ${this.reverseLookup(CanvasOp, entry.op)} (${entry.op}) on view ${
          state.view.uniqueId
        }`,
        entry,
        state,
      );
      return;
    }

    renderer.render(state, <CanvasAndroid>entry, state.saveStack[0].c2d);
  }
}

// TODO(mmastrac): This is duplicated code from the Android renderer, needs to be moved to a shared base class
abstract class Renderer {
  abstract render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D);

  protected drawMissingImage(c2d: CanvasRenderingContext2D, dst: Rect) {
    c2d.save();
    c2d.lineWidth = dst.width() > 200 && dst.height() > 200 ? 3 : 1;
    c2d.fillStyle = '#eeeeee';
    c2d.fillRect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.strokeStyle = '#aaaaaa';
    c2d.beginPath();
    c2d.rect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.stroke();
    c2d.setLineDash([3, 3]);
    c2d.moveTo(dst.x1, dst.y1);
    c2d.lineTo(dst.x2, dst.y2);
    c2d.moveTo(dst.x1, dst.y2);
    c2d.lineTo(dst.x2, dst.y1);
    c2d.stroke();
    c2d.restore();
  }

  protected drawPrivateImage(c2d: CanvasRenderingContext2D, dominantColor: string, dst: Rect) {
    c2d.save();
    c2d.fillStyle = dominantColor;
    c2d.fillRect(dst.x1, dst.y1, dst.width(), dst.height());
    c2d.restore();
  }
}

/**
 * If the operation specifies a paint mode, draws the shape to an offscreen
 * buffer, applies shaders and then draws it to the main canvas.
 *
 * In some cases where the shader can be applied directly as a fill/stroke
 * we can skip the offscreen buffer.
 */
abstract class PaintableRenderer extends Renderer {
  static mapPorterDuff(mode: number): GlobalCompositeOperation {
    switch (mode) {
      case PorterDuffMode.CLEAR:
        // TODO(mmastrac): not correct, needs thought
        // 'clear' in HTML canvas appears to be different from Android's clear. Hmm.
        return 'destination-out';
      case PorterDuffMode.SRC:
        return 'copy';
      case PorterDuffMode.SRC_IN:
        return 'source-in';
      case PorterDuffMode.SRC_ATOP:
        return 'source-atop';
      case PorterDuffMode.SRC_OUT:
        return 'source-out';
      case PorterDuffMode.SRC_OVER:
        return 'source-over';
      case PorterDuffMode.UNAVAILABLE:
        return 'source-over';
      case PorterDuffMode.DST:
        return null; // There's no 'destination' so we have to handle it in the called
      case PorterDuffMode.DST_IN:
        return 'destination-in';
      case PorterDuffMode.DST_ATOP:
        return 'destination-atop';
      case PorterDuffMode.DST_OUT:
        return 'destination-out';
      case PorterDuffMode.DST_OVER:
        return 'destination-over';
      case PorterDuffMode.XOR:
        return 'xor';
      case PorterDuffMode.DARKEN:
        // TODO(mmastrac): This isn't correct, but since we can't specify blend+composite this is "close enough"
        return 'source-over';
      case PorterDuffMode.LIGHTEN:
        // TODO(mmastrac): This isn't correct, but since we can't specify blend+composite this is "close enough"
        return 'xor';
      case PorterDuffMode.MULTIPLY:
        // TODO(mmastrac): This isn't correct, but since we can't specify blend+composite this is "close enough"
        return 'source-in';
      case PorterDuffMode.SCREEN:
        // TODO(mmastrac): This isn't correct, but since we can't specify blend+composite this is "close enough"
        return 'source-over';
    }
    logWarnOnce(`Unhandled porter-duff mode: ${mode}, using source-in`);
    return 'source-in';
  }

  // This may be overridden by subclasses to use a smaller off-screen temp canvas. (Saves work when the view is larger than the screen.)
  // It can also prevent issues when the temp canvas needs to be *larger* than the view (e.g. when a transform is in play)
  getDimensions(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [number, number] {
    // todo(nathan): if a transform is active, take that into account when determining size
    return [state.width, state.height];
  }

  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const [width, height] = this.getDimensions(state, op, c2d);
    if (width <= 0 || height <= 0) {
      // skip drawing zero-sized things
      return;
    }

    if (op.op == CanvasOp.DRAW_BITMAP_POS || op.op == CanvasOp.DRAW_BITMAP_RECT) {
      if (op.flag1 != 1) {
        // Bitmaps don't have shaders
        // TODO(mmastrac): Don't send these from the server
        delete op.paint.bitmap;
        delete op.paint.shader;
      }
    }

    if (
      op.paint.bitmap ||
      op.paint.shader ||
      op.paint.porterDuffColorMode != null ||
      op.paint.porterDuffXferMode != null
    ) {
      const matrix = state.saveStack[0].matrix;
      const useMatrix = !Matrix.isIdentity(matrix);
      let temp2d;
      if (useMatrix) {
        // when a matrix is in play, set the offscreen canvas to the same size as the view's and apply the same matrix to it
        // later, when copying back to the view's canvas, we'll invert the matrix
        // see below for allowLarger note
        temp2d = OffscreenCanvasCache.getC2d(state.width, state.height, { allowLarger: true });
        Matrix.setMatrix(matrix, temp2d);
      } else {
        // allowLarger note:
        // Prior to the OffscreenCanvasCache, the temp canvas was 2x the height and width of the view.
        // That was usually far larger than necessary, but occasionally too small.
        // The width and height are now calculated to the exact size needed or else set to 1x the size of the view.
        // Regardless, it is assumed that a larger-than-requested temp canvas will be safe.
        // Any code that uses this temp canvas must
        //  - use a clip mask on the destination and/or
        //  - be explicit about what pixels to copy from the temp canvas
        // (Allowing a larger canvas can improve performance by reducing allocations and subsequent garbage collection.)
        temp2d = OffscreenCanvasCache.getC2d(width, height, { allowLarger: true });
      }
      temp2d.save();
      this.renderTo(state, op, temp2d);

      if (op.paint.shader) {
        const shader = op.paint.shader;
        const type = shader.type || ShaderType.LINEAR_GRADIENT;
        if (type == ShaderType.LINEAR_GRADIENT) {
          const gradient = temp2d.createLinearGradient(
            shader.p1.x,
            shader.p1.y,
            shader.p2.x,
            shader.p2.y,
          );
          const positions = shader.positions.length ? shader.positions : [0, 1];
          for (let i = 0; i < positions.length; i++) {
            gradient.addColorStop(positions[i], shader.colors[i]);
          }
          temp2d.fillStyle = gradient;

          temp2d.globalCompositeOperation = 'source-in';
          temp2d.fillRect(0, 0, state.width, state.height);
        } else if (type == ShaderType.RADIAL_GRADIENT) {
          const gradient = temp2d.createRadialGradient(
            shader.p1.x,
            shader.p1.y,
            0,
            shader.p1.x,
            shader.p1.y,
            shader.r,
          );
          const positions = shader.positions.length ? shader.positions : [0, 1];
          for (let i = 0; i < positions.length; i++) {
            gradient.addColorStop(positions[i], shader.colors[i]);
          }
          temp2d.fillStyle = gradient;

          temp2d.globalCompositeOperation = 'source-in';
          temp2d.fillRect(0, 0, state.width, state.height);
        } else if (type == ShaderType.SWEEP_GRADIENT) {
          // empty block
        }
      } else if (op.paint.bitmap) {
        const bm = op.paint.bitmap;
        if (!bm.isValid()) {
          this.drawMissingImage(temp2d, new Rect(0, 0, state.width, state.height));
        } else if (bm.type == BitmapType.Masked) {
          this.drawPrivateImage(temp2d, bm.dominantColor, new Rect(0, 0, bm.width, bm.height));
        } else {
          temp2d.globalCompositeOperation = 'source-in';
          temp2d.save();
          if (op.paint.shaderMatrix) {
            Matrix.appendMatrix(op.paint.shaderMatrix, temp2d);
          }
          const scale = state.view.flag(Flags.HARDWARE_ACCELERATED)
            ? 1
            : state.view.frame.density / bm.density;
          temp2d.scale(scale, scale);
          const repeatX = op.paint.tileX == TileMode.REPEAT;
          const repeatY = op.paint.tileY == TileMode.REPEAT;
          if (!repeatX && !repeatY) {
            temp2d.drawImage(bm.img, 0, 0, bm.width, bm.height);
          } else {
            let repeatMode;
            if (repeatX) {
              repeatMode = repeatY ? 'repeat' : 'repeat-x';
            } else {
              repeatMode = 'repeat-y';
            }
            const pattern = temp2d.createPattern(bm.img, repeatMode);
            temp2d.fillStyle = pattern;
            temp2d.fillRect(0, 0, state.width / scale, state.height / scale);
          }
          temp2d.restore();
        }
      }

      // todo(Nathan): do we need to run this if there was an op.paint.shader or op.paint.bitmap? (They both force source-in mode.)
      if (op.paint.porterDuffColorMode != null) {
        const compositeOp = PaintableRenderer.mapPorterDuff(op.paint.porterDuffColorMode);
        if (compositeOp) {
          temp2d.globalCompositeOperation = compositeOp;

          temp2d.save();
          if (op.paint.shaderMatrix) {
            Matrix.appendMatrix(op.paint.shaderMatrix, temp2d);
          }

          temp2d.fillStyle = op.paint.porterDuffColor;
          temp2d.fillRect(0, 0, width, height);

          temp2d.restore();
        }
      }

      if (op.paint.porterDuffXferMode != null) {
        const compositeOp = PaintableRenderer.mapPorterDuff(op.paint.porterDuffXferMode);
        // If someone wants to use the SRC xfer mode, we just do nothing
        if (!compositeOp) return;
        c2d.save();
        c2d.globalCompositeOperation = compositeOp;
      }

      if (useMatrix) {
        c2d.save();
        Matrix.appendMatrix(Matrix.invert(matrix), c2d);
        c2d.drawImage(temp2d.canvas, 0, 0);
        c2d.restore();
      } else {
        c2d.drawImage(temp2d.canvas, 0, 0);
      }
      temp2d.restore();
      OffscreenCanvasCache.putC2d(temp2d);
      temp2d = null;

      if (op.paint.porterDuffXferMode != null) {
        c2d.restore();
      }

      return;
    }

    this.renderTo(state, op, c2d);
  }

  renderTo(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    // Preconfigure the colors for the renderer
    c2d.lineWidth = op.paint.strokeWidth || 1;
    c2d.strokeStyle = op.paint.color;
    c2d.fillStyle = op.paint.color;
    c2d.beginPath();

    const ret = this.renderPath(state, op, c2d);
    const [forceStroke, path] = ret ? ret : [false, null];

    const style = op.paint.style;

    if (style == 1 || style == 2 || forceStroke) {
      if (path) {
        c2d.stroke(path);
      } else {
        c2d.stroke();
      }
    }
    if (style == 0 || style == 2) {
      if (path) {
        c2d.fill(path, op.paint.fillRule);
      } else {
        c2d.fill(op.paint.fillRule);
      }
    }
  }

  /**
   * The subclass must render a path on the given context -- this superclass will figure out how to stroke and/or
   * fill it.
   */
  abstract renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D];
}

class DrawChildRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    state.drawChild(op.viewId, state.saveStack[0].matrix);
  }
}

class ClipRectRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    // TODO(mmastrac): Also needs to correctly handle transform
    // instead of assuming translation
    if (op.flag1 > 1) {
      logWarnOnce(
        `Unhandled clip op: ${op.flag1} on view ${state.view.uniqueId}. This is deprecated on Android P and later`,
        op,
      );
    }

    // See this PR for more context on how this works: https://github.com/cowpaths/mn/pull/24292
    // Also see the deprecation notice in Android for canvas.clipRect on operations other than
    // INTERSECT and DIFFERENCE
    // https://developer.android.com/reference/android/graphics/Canvas#clipRect(float,%20float,%20float,%20float,%20android.graphics.Region.Op)
    if (op.flag1 == RegionOp.DIFFERENCE) {
      const region = new Path2D();
      region.rect(0, 0, c2d.canvas.width, c2d.canvas.height);
      region.rect(op.src.x1, op.src.y1, op.src.width(), op.src.height());
      c2d.clip(region, 'evenodd');
    } else {
      // Region.OP.INTERSECT
      c2d.beginPath();
      c2d.rect(op.src.x1, op.src.y1, op.src.width(), op.src.height());
      c2d.clip();
    }
  }
}

class ClipPathRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    if (op.flag1 > RegionOp.INTERSECT) {
      logWarnOnce(
        `Unhandled clip op: ${op.flag1} on view ${state.view.uniqueId}. This is deprecated on Android P and later`,
        op,
      );
    }
    if (op.flag1 == RegionOp.DIFFERENCE) {
      c2d.clip(op.path, 'evenodd');
    } else {
      c2d.clip(op.path);
    }
  }
}

class WarningRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    console.error(op.text);
  }
}

class SaveRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const top = state.saveStack[0];
    state.saveStack.unshift({
      matrix: top.matrix.slice(),
      c2d: top.c2d,
      bag: {},
      fn: entry => {
        entry.c2d.restore();
      },
    });
    c2d.save();
  }
}

class SaveLayerRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const top = state.saveStack[0];
    let tempC2d = OffscreenCanvasCache.getC2d(state.width, state.height);

    state.saveStack.unshift({
      matrix: top.matrix.slice(),
      c2d: tempC2d,
      bag: {},
      fn: entry => {
        // Restore back to the state we had before our call to save()
        c2d.restore();

        c2d.save();
        c2d.globalAlpha = op.paint.alpha;
        c2d.drawImage(tempC2d.canvas, 0, 0);
        c2d.restore();

        OffscreenCanvasCache.putC2d(tempC2d);
        tempC2d = null;
      },
    });
    c2d.save();
  }
}

class SaveLayerAlphaRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const top = state.saveStack[0];
    let tempC2d = OffscreenCanvasCache.getC2d(state.width, state.height);

    state.saveStack.unshift({
      matrix: top.matrix.slice(),
      c2d: tempC2d,
      bag: {},
      fn: entry => {
        // Restore back to the state we had before our call to save()
        c2d.restore();

        c2d.save();
        c2d.globalAlpha = op.paint.alpha;
        c2d.drawImage(tempC2d.canvas, 0, 0);
        c2d.restore();

        OffscreenCanvasCache.putC2d(tempC2d);
        tempC2d = null;
      },
    });
    c2d.save();
  }
}

class RestoreToCountRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    let pos = op.flag1;

    // https://code.google.com/p/skia/source/browse/trunk/src/core/SkCanvas.cpp?r=489
    if (pos < 1) pos = 1;

    // If we're restoring to position 1, that means we're going to pop everything
    // but the first element in the save stack
    while (state.saveStack.length > pos) {
      state.pop();
    }
  }
}

class RestoreRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    if (state.saveStack.length > 1) {
      state.pop();
    } else {
      logWarnOnce(
        `Busted restore (no saved state to restore to) on view ${state.view.uniqueId}`,
        op,
      );
    }
  }
}

class TranslateRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    // aka preTranslate
    const m = Matrix.translation(op.point.x, op.point.y);
    state.saveStack[0].matrix = Matrix.multiply(m, state.saveStack[0].matrix);

    c2d.translate(op.point.x, op.point.y);
  }
}

class ScaleRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    // aka preScale
    const m = Matrix.scale(op.point.x, op.point.y);
    state.saveStack[0].matrix = Matrix.multiply(m, state.saveStack[0].matrix);

    c2d.scale(op.point.x, op.point.y);
  }
}

class RotateRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const rad = (op.value || 0) * (Math.PI / 180);
    const m = Matrix.rotation(rad);
    state.saveStack[0].matrix = Matrix.multiply(m, state.saveStack[0].matrix);

    c2d.rotate(rad);
  }
}

class SetMatrixRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const m = Matrix.affinize(op.matrix);
    state.saveStack[0].matrix = m;
    Matrix.setMatrix(m, c2d);
  }
}

class ConcatMatrixRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    const m = Matrix.affinize(op.matrix);
    state.saveStack[0].matrix = Matrix.multiply(m, state.saveStack[0].matrix);
    Matrix.appendMatrix(m, c2d);
  }
}

class DrawColorRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    c2d.save();
    c2d.fillStyle = op.paint.color;
    // TODO(mmastrac): We should also support alternate porter-duff modes
    // TODO(mmastrac|dave): returning this to default src-over, 'copy' breaks some transparency cases (homeaway) - but we should also
    //  support alternate porter-duff modes if they are specified.
    // TODO(mmastrac): Ok, now we support alternate porter-duff modes, but we need to re-test homeaway
    // TODO(mmastrac): Well SRC_IN probably breaks a bunch of stuff until we get offscreen rendering working for all views
    c2d.globalCompositeOperation =
      op.flag1 === PorterDuffMode.SRC_IN
        ? 'source-over'
        : PaintableRenderer.mapPorterDuff(op.flag1);
    // Fill the entire canvas behind the view
    c2d.fillRect(0, 0, state.view.position.width(), state.view.position.height());
    c2d.restore();
  }
}

class DrawPaintRenderer extends PaintableRenderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    c2d.save();
    super.render(state, op, c2d);
    c2d.restore();
  }

  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    // Fill the entire canvas behind the view
    c2d.fillRect(0, 0, state.view.position.width(), state.view.position.height());
    logOnce(`TODO: Confirm that DRAW_PAINT is working properly on view ${state.view.uniqueId}`, op);
    return null;
  }
}

class DrawPathRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    const path = op.path;

    // TODO(mmastrac): This indicates we couldn't serialize a path server-side - do or log something
    if (!path) {
      return null;
    }

    return [false, path];
  }
}

class DrawLineRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    const line = op.dest;
    c2d.moveTo(line.x1, line.y1);
    c2d.lineTo(line.x2, line.y2);

    // Always stroke lines
    return [true, null];
  }
}

class DrawCircleRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    if (op.value > 0) {
      // in Android <=0 is a no-op, but in browsers <0 is an error.
      c2d.arc(op.point.x, op.point.y, op.value, 0, Math.PI * 2);
    }
    return null;
  }
}

/**
 * We draw a squished circle instead of an oval because of poor support for canvas ellipse().
 */
class DrawOvalRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    const cx = (op.dest.x2 + op.dest.x1) / 2;
    const cy = (op.dest.y2 + op.dest.y1) / 2;
    const xScale = op.dest.width();
    const yScale = op.dest.height();

    c2d.save();
    // Perform the translation before the scale rather than scaling the center in the .arc() call
    c2d.translate(cx, cy);
    c2d.scale(xScale, yScale);
    c2d.arc(0, 0, 0.5, 0, Math.PI * 2, false);
    c2d.restore();
    return null;
  }
}

class DrawArcRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    const cx = (op.dest.x2 + op.dest.x1) / 2;
    const cy = (op.dest.y2 + op.dest.y1) / 2;

    // The android drawArc documentation is confusing, but essentially a negative sweep angle
    // means counter-clockwise
    let end, start: number;
    if (op.point.y < 0) {
      end = op.point.x * (Math.PI / 180);
      start = end + op.point.y * (Math.PI / 180);
    } else {
      start = op.point.x * (Math.PI / 180);
      end = start + op.point.y * (Math.PI / 180);
    }
    const includeCenter = op.flag1 == 1;
    const xScale = op.dest.width();
    const yScale = op.dest.height();

    c2d.save();
    // Perform the translation before the scale rather than scaling the center in the .arc() call
    c2d.translate(cx, cy);
    c2d.scale(xScale, yScale);
    if (includeCenter) c2d.moveTo(0, 0);
    c2d.arc(0, 0, 0.5, start, end, false);
    if (includeCenter) c2d.lineTo(0, 0);
    c2d.restore();
    return null;
  }
}

// get an constrained height/width for things that use op.dest to decide where to draw
function getDimensionsForDest(state, op, c2d): [number, number] {
  // default to destination x2/y2 rather than height()/width() so that something with a non-zero x1/y1 will still be drawn on the canvas
  // todo(nathan): look into using translation and a smaller canvas to achieve the same effect with less memory used
  let width = op.dest.x2;
  let height = op.dest.y2;
  // if no transform is in play, constrain the size to the smaller of:
  //   1) the destination
  //   2) the size of the current view
  //   3) the size of the parent canvas (usually the onscreen canvas)
  const isIdentityMatrix = Matrix.isIdentity(state.saveStack[0].matrix);
  if (isIdentityMatrix) {
    width = Math.min(width, state.width, c2d.canvas.width);
    height = Math.min(height, state.height, c2d.canvas.height);
  }
  return [width, height];
}
class DrawRectRenderer extends PaintableRenderer {
  getDimensions = getDimensionsForDest;
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    const dst = op.dest;
    c2d.rect(dst.x1, dst.y1, dst.width(), dst.height());
    return null;
  }
}

class DrawRoundRectRenderer extends PaintableRenderer {
  getDimensions = getDimensionsForDest;
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    let rx = op.point.x,
      ry = op.point.y;
    const dst = op.dest;

    // Don't bother with really tiny radii
    if (rx < 0.001 || ry < 0.001) {
      c2d.rect(dst.x1, dst.y1, dst.width(), dst.height());
      return null;
    }

    // Clamp radius to 1/2 dimension via W3C section 5.5 algorithm - Overlapping Curves
    // "Corner curves must not overlap: When the sum of any two adjacent border radii
    // exceeds the size of the border box, UAs must proportionally reduce the used values
    // of all border radii until none of them overlap."
    if (rx * 2 > op.dest.width()) {
      const ratio = op.dest.width() / (rx * 2);
      rx *= ratio;
      ry *= ratio;
    }
    if (ry * 2 > op.dest.height()) {
      const ratio = op.dest.height() / (ry * 2);
      rx *= ratio;
      ry *= ratio;
    }

    // Clamp to zero
    rx = Math.max(0, rx);
    ry = Math.max(0, ry);

    // http://whizkidtech.redprince.net/bezier/circle/
    // http://whizkidtech.redprince.net/bezier/circle/kappa/
    const kappa = 0.5522847498307936;
    const lx = rx * kappa;
    const ly = ry * kappa;

    // Much more readable if we avoid struct accessors
    const x1 = dst.x1,
      x2 = dst.x2,
      y1 = dst.y1,
      y2 = dst.y2;

    // Left
    c2d.moveTo(x1, y1 + ry);
    c2d.lineTo(x1, y2 - ry);

    // Bottom
    c2d.bezierCurveTo(x1, y2 - ry + ly, x1 + rx - lx, y2, x1 + rx, y2);
    c2d.lineTo(x2 - rx, y2);

    // Right
    c2d.bezierCurveTo(x2 - rx + lx, y2, x2, y2 - ry + ly, x2, y2 - ry);
    c2d.lineTo(x2, y1 + ry);

    // Top
    c2d.bezierCurveTo(x2, y1 + ry - ly, x2 - rx + lx, y1, x2 - rx, y1);
    c2d.lineTo(x1 + rx, y1);

    // Top left corner
    c2d.bezierCurveTo(x1 + rx - lx, y1, x1, y1 + ry - ly, x1, y1 + ry);

    c2d.closePath();
    return null;
  }
}

class DrawViewRenderer extends Renderer {
  // This is a custom canvas op that we use for views that can't be rendered as canvas
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    if (!op.bitmap || !op.bitmap.isValid()) {
      logWarnOnce(`Missing or invalid bitmap ${op.bitmap} on view ${state.view.uniqueId}`, op);
      this.drawMissingImage(c2d, op.dest);
      return;
    }

    if (op.bitmap.type == BitmapType.Masked) {
      this.drawPrivateImage(c2d, op.bitmap.dominantColor, op.dest);
      return;
    }

    const dst = op.dest;
    c2d.drawImage(op.bitmap.img, dst.x1, dst.y1, dst.width(), dst.height());
  }
}

class DrawBitmapPosRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    if (!op.bitmap || !op.bitmap.isValid()) {
      logWarnOnce(`Missing or invalid bitmap on view ${state.view.uniqueId}`, op);
      // We don't know the bounds here so we can't draw a placeholder
      return null;
    }

    if (op.bitmap.type == BitmapType.Masked) {
      // TODO: What do we draw here?
      return null;
    }

    // Adjust this bitmap's size based on the relative density iff this is not a hardware canvas
    // TODO(mmastrac): I don't know if this logic is 100% correct here
    const scale = state.view.flag(Flags.HARDWARE_ACCELERATED)
      ? 1
      : state.view.frame.density / op.bitmap.density;

    const scaledX = scale * op.bitmap.width,
      scaledY = scale * op.bitmap.height;

    c2d.save();
    c2d.globalAlpha *= op.paint.alpha;
    if (op.matrix) {
      const m = Matrix.affinize(op.matrix);
      Matrix.appendMatrix(m, c2d);
    }
    c2d.drawImage(op.bitmap.img, op.point.x, op.point.y, scaledX, scaledY);
    c2d.restore();
    return null;
  }
}

class DrawBitmapRectRenderer extends PaintableRenderer {
  getDimensions = getDimensionsForDest;
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    if (!op.bitmap || !op.bitmap.isValid()) {
      logWarnOnce(`Missing or invalid bitmap ${op.bitmap} on view ${state.view.uniqueId}`, op);
      this.drawMissingImage(c2d, op.dest);
      return null;
    }

    if (op.bitmap.type == BitmapType.Masked) {
      this.drawPrivateImage(c2d, op.bitmap.dominantColor, op.dest);
      return null;
    }

    c2d.save();
    c2d.globalAlpha *= op.paint.alpha;
    let src = op.src;
    const dst = op.dest;
    // If this bitmap was clamped in size, we need to scale our src bounds
    if (op.bitmap.width != op.bitmap.img.width || op.bitmap.height != op.bitmap.img.height) {
      const scaleX = op.bitmap.img.width / op.bitmap.width;
      const scaleY = op.bitmap.img.height / op.bitmap.height;
      if (src.isEmpty()) {
        // New vector drawable code case
        // TODO(mmastrac): All of these bitmap paths are a big mess
        if (op.bitmap.type == BitmapType.RasterizedVector) {
          src = new Rect(
            0,
            0,
            Math.min(op.bitmap.width, op.bitmap.img.width),
            Math.min(op.bitmap.height, op.bitmap.img.height),
          );
        }
      } else {
        src = src.scale(scaleX, scaleY);
      }
    }
    if (src && !src.isEmpty()) {
      c2d.drawImage(
        op.bitmap.img,
        src.x1,
        src.y1,
        src.width(),
        src.height(),
        dst.x1,
        dst.y1,
        dst.width(),
        dst.height(),
      );
    } else {
      c2d.drawImage(op.bitmap.img, dst.x1, dst.y1, dst.width(), dst.height());
    }
    c2d.restore();
    return null;
  }
}

class DrawPatchRenderer extends PaintableRenderer {
  renderPath(
    state: CanvasState,
    op: CanvasAndroid,
    c2d: CanvasRenderingContext2D,
  ): [boolean, Path2D] {
    const dst = op.dest;

    if (!op.bitmap || !op.bitmap.isValid()) {
      logWarnOnce(`Missing or invalid bitmap ${op.bitmap} on view ${state.view.uniqueId}`, op);
      this.drawMissingImage(c2d, op.dest);
      return null;
    }

    if (op.bitmap.type == BitmapType.Masked) {
      this.drawPrivateImage(c2d, op.bitmap.dominantColor, op.dest);
      return null;
    }

    if (!op.bitmap.ninePatch) {
      logWarnOnce(`Missing ninePatch for ${op.bitmap.url} on view ${state.view.uniqueId}`, op);
      return null;
    }

    // Don't trust the size seen by the client as it might have been DPI-scaled: use the actual resource size
    const ninePatch = new NinePatch(op.bitmap.ninePatch, op.bitmap.img.width, op.bitmap.img.height);

    c2d.save();
    c2d.globalAlpha *= op.paint.alpha;
    c2d.translate(dst.x1, dst.y1);

    // Nine-patch images should be drawn without bilinear filtering (the Weather App uses a tiny nine-patch to
    // fake out a border
    (<any>c2d).imageSmoothingEnabled = false;
    (<any>c2d).mozImageSmoothingEnabled = false;
    (<any>c2d).webkitImageSmoothingEnabled = false;

    const scale = op.bitmap.width / op.bitmap.img.width;
    c2d.scale(scale, scale);

    ninePatch.draw(op.bitmap.img, c2d, ~~(dst.width() / scale), ~~(dst.height() / scale));

    c2d.restore();
    return null;
  }
}

class DrawTextRenderer extends Renderer {
  render(state: CanvasState, op: CanvasAndroid, c2d: CanvasRenderingContext2D) {
    // No-op
    // TODO(mmastrac): Should probably not be sending these from the client
    if (op.text == null || op.text.length == 0 || op.paint.textBounds == null) {
      logWarnOnce(`Null or empty text for DrawTextRenderer on view ${state.view.uniqueId}`, op);
      return;
    }

    let style = '';
    if (op.paint.textStyle & TextStyleFlags.BOLD) style += 'bold ';
    if (op.paint.textStyle & TextStyleFlags.ITALIC) style += 'italic ';

    const path = op.path;

    c2d.save();

    let font = 'Roboto';
    if (op.paint.typeface) {
      font = `'dynamic-${op.paint.typeface.getName().replace(/'/g, '')}'`;
    }

    c2d.font = `${style + op.paint.size}px ${font}`;
    c2d.fillStyle = op.paint.color;
    c2d.textBaseline = 'alphabetic';

    // Measure the width of the string after we've set up the font
    const width = c2d.measureText(op.text).width;

    const x = op.point.x;
    const y = op.point.y;

    if (path) {
      // TODO(mmastrac): We don't support rendering on arbitrary paths yet
      if (!this.isLinearPath(path)) {
        logWarnOnce(`Unsupported non-linear text path on view ${state.view.uniqueId}`, op);
        c2d.restore();
        return;
      }

      // putting variable declarations outside if statement
      let x1: any;
      let x2: any;
      let y1: any;
      let y2: any;

      // Simple linear path -- convert to a matrix
      if (path.verbs) {
        x1 = path.points[0];
        x2 = path.points[2];
        y1 = path.points[1];
        y2 = path.points[3];
      } else {
        x1 = path[1];
        x2 = path[4];
        y1 = path[2];
        y2 = path[5];
      }

      const adj = x2 - x1;
      const opp = y2 - y1;
      const hyp = Math.sqrt(Math.pow(opp, 2) + Math.pow(adj, 2));
      const cos = adj / hyp;
      const sin = opp / hyp;

      // Matrix is [cos, -sin, x1], [sin, cos, y1], [0, 0, 1]
      c2d.transform(cos, sin, -sin, cos, x1, y1);

      // Center text?
      if (op.paint.align == 1) {
        c2d.translate((hyp - width) / 2, 0);
      }
    } else {
      // Center text?
      // eslint-disable-next-line no-lonely-if
      if (op.paint.align == 1) {
        c2d.translate(-width / 2, 0);
      }
    }

    // Match width generated on client
    c2d.translate(x, y);
    c2d.scale(op.paint.textBounds.x / width, 1);
    if (op.textPrivate) {
      // TODO(mmastrac): This is a quick & dirty attempt
      const oldAlpha = c2d.globalAlpha;
      c2d.globalAlpha *= 0.75;
      c2d.beginPath();
      c2d.roundRect(
        0,
        -op.paint.textBounds.y,
        width,
        op.paint.textBounds.y,
        op.paint.textBounds.y * 0.25,
      );
      c2d.fill();
      c2d.globalAlpha = oldAlpha;
    } else {
      c2d.fillText(op.text, 0, 0);
    }

    // We have to fake underlining
    // Underline isn't supported when text is stroked on a path so we won't draw it (see https://code.google.com/p/android/issues/detail?id=42437)
    if (op.paint.textStyle & TextStyleFlags.UNDERLINE && !path) {
      const underlineOffset = ~~(op.paint.size / 10);
      const underlineY = ~~underlineOffset;

      c2d.beginPath();
      c2d.moveTo(0, underlineY);
      c2d.lineTo(width, underlineY);

      c2d.lineWidth = 2;
      c2d.strokeStyle = op.paint.color;
      c2d.stroke();
    }

    if (op.paint.textStyle & TextStyleFlags.STRIKETHROUGH && !path) {
      const strikethroughY = -op.paint.size / 3;

      c2d.beginPath();
      c2d.moveTo(0, strikethroughY);
      c2d.lineTo(width, strikethroughY);

      c2d.lineWidth = 2;
      c2d.strokeStyle = op.paint.color;
      c2d.stroke();
    }

    c2d.restore();
  }

  isLinearPath(path): boolean {
    if (path.verbs) {
      return path.verbs.length == 2 && path.verbs[0] == 0 && path.verbs[1] == 1;
    } else {
      return path.length == 6;
    }
  }
}
